import React, {
    useRef,
    useState,
    useEffect,
    HTMLProps,
    useImperativeHandle,
    forwardRef,
    ForwardedRef,
} from 'react';
import noop from 'lodash/noop';
import useMountedState from 'react-use/lib/useMountedState';
import { CSSProperties } from 'styled-components';
import { useIsRobot } from '../../utils/RobotContext';
import { BreakpointProp } from '../../utils/styles/breakpoints';
import { withLazyLoad } from '../../utils/withLazyLoad';
import { Skeleton } from '../feedback/Skeleton';
import { Img } from './styles';

export interface ImageProps extends HTMLProps<HTMLImageElement> {
    objectFit?: BreakpointProp<CSSProperties['objectFit']>;
    objectPosition?: BreakpointProp<CSSProperties['objectPosition']>;
    maxWidth?: BreakpointProp<CSSProperties['maxWidth']>;
}

export const Image = withLazyLoad(
    forwardRef((props: ImageProps, ref: ForwardedRef<HTMLImageElement | null>) => {
        const { isRobot } = useIsRobot();
        const isMounted = useMountedState();
        // без гидрации событие load не наступит, а картинка так и останется прозрачной
        const [isLoaded, setIsLoaded] = useState(isRobot);
        const innerRef = useRef<HTMLImageElement | null>(null);

        useImperativeHandle<HTMLImageElement | null, HTMLImageElement | null>(
            ref,
            () => innerRef.current,
        );

        useEffect(() => {
            if (!innerRef.current) {
                return noop;
            }

            if (!innerRef.current.complete) {
                const onImageLoaded = () => {
                    if (isMounted()) {
                        setIsLoaded(true);
                    }
                };

                innerRef.current.addEventListener('load', onImageLoaded);
                innerRef.current.addEventListener('error', props.onError ?? noop);

                return () => {
                    innerRef.current?.removeEventListener('load', onImageLoaded);
                    // eslint-disable-next-line react-hooks/exhaustive-deps
                    innerRef.current?.removeEventListener('error', props.onError ?? noop);
                };
            }

            setIsLoaded(true);

            return noop;
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [innerRef.current]);

        return <Img {...props} ref={innerRef} isLoaded={isLoaded} />;
    }),
    forwardRef((_, ref: ForwardedRef<HTMLDivElement>) => (
        <Skeleton variant="rect" width="100%" height="100%" ref={ref} />
    )),
);

Image.defaultProps = {
    objectFit: 'cover',
};
